/**
 * AdminShop — Product CRUD + Orders + Shop config in one component.
 */
import { useEffect, useMemo, useState } from "react";
import { api, errMsg, API_BASE } from "@/lib/config";
import { toast } from "sonner";
import { SHOP_CATEGORIES } from "@/pages/Shop";
import { Package, Plus, Trash2, Edit3, X, Save, Loader2, Truck, ShoppingBag,
         Settings, UploadCloud, Check, Calculator, ImagePlus } from "lucide-react";

const STEEL_DENSITY = 7.9; // g/cm³ 304 stainless
const fmtEur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const fullMediaUrl = (u) => (u?.startsWith("/api/") ? `${API_BASE}${u}` : u || "");
const uid = () => Math.random().toString(36).slice(2, 12);

/* ─── Weight calculator ─── */
function calcWeight(shape, d, density = STEEL_DENSITY) {
  const t = (d.thickness_mm || 0) / 10;
  switch (shape) {
    case "sphere_hollow": {
      const ro = (d.diameter_mm / 2) / 10;
      const ri = Math.max(0, ro - t);
      return (4 / 3) * Math.PI * (ro ** 3 - ri ** 3) * density;
    }
    case "sphere_solid": {
      const r = (d.diameter_mm / 2) / 10;
      return (4 / 3) * Math.PI * r ** 3 * density;
    }
    case "plate": {
      const L = (d.length_mm || 0) / 10;
      const W = (d.width_mm || 0) / 10;
      return L * W * t * density;
    }
    case "cube_solid": {
      return ((d.length_mm || 0) / 10) * ((d.width_mm || 0) / 10) * ((d.height_mm || 0) / 10) * density;
    }
    case "cube_hollow": {
      const L = (d.length_mm || 0) / 10;
      const W = (d.width_mm || 0) / 10;
      const H = (d.height_mm || 0) / 10;
      const inner = Math.max(0, (L - 2 * t) * (W - 2 * t) * (H - 2 * t));
      return (L * W * H - inner) * density;
    }
    default: return 0;
  }
}

function WeightCalculator({ onCompute }) {
  const [shape, setShape] = useState("sphere_hollow");
  const [d, setD] = useState({ diameter_mm: 100, thickness_mm: 0.8, length_mm: 100, width_mm: 100, height_mm: 30 });
  const patch = (k, v) => setD({ ...d, [k]: parseFloat(v) || 0 });
  const g = calcWeight(shape, d);
  return (
    <div className="border border-[#1a1a1a] p-4 bg-[#0a0a0a]" data-testid="weight-calc">
      <div className="flex items-center gap-2 mb-3">
        <Calculator size={12} className="text-[#ff4b00]" />
        <div className="font-mono-tech text-[10px] uppercase text-white/60">Calcolatore peso (Inox · 7,9 g/cm³)</div>
      </div>
      <select value={shape} onChange={(e) => setShape(e.target.value)} className="field-boxed mb-3">
        <option value="sphere_hollow">Sfera cava (pallina)</option>
        <option value="sphere_solid">Sfera piena</option>
        <option value="plate">Piastra / decoro piatto</option>
        <option value="cube_solid">Cubo/parallelepipedo pieno</option>
        <option value="cube_hollow">Cubo/parallelepipedo cavo (scatola)</option>
      </select>
      <div className="grid grid-cols-2 gap-3 text-xs">
        {(shape === "sphere_hollow" || shape === "sphere_solid") && (
          <div><label className="field-label">Ø diametro (mm)</label><input type="number" step="0.1" className="field-boxed mt-1" value={d.diameter_mm} onChange={(e) => patch("diameter_mm", e.target.value)} /></div>
        )}
        {(shape === "sphere_hollow" || shape === "plate" || shape === "cube_hollow") && (
          <div><label className="field-label">Spessore (mm)</label><input type="number" step="0.1" className="field-boxed mt-1" value={d.thickness_mm} onChange={(e) => patch("thickness_mm", e.target.value)} /></div>
        )}
        {(shape === "plate" || shape === "cube_solid" || shape === "cube_hollow") && (
          <><div><label className="field-label">Lunghezza (mm)</label><input type="number" step="0.1" className="field-boxed mt-1" value={d.length_mm} onChange={(e) => patch("length_mm", e.target.value)} /></div>
          <div><label className="field-label">Larghezza (mm)</label><input type="number" step="0.1" className="field-boxed mt-1" value={d.width_mm} onChange={(e) => patch("width_mm", e.target.value)} /></div></>
        )}
        {(shape === "cube_solid" || shape === "cube_hollow") && (
          <div><label className="field-label">Altezza (mm)</label><input type="number" step="0.1" className="field-boxed mt-1" value={d.height_mm} onChange={(e) => patch("height_mm", e.target.value)} /></div>
        )}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="font-display text-2xl text-[#ff4b00]" data-testid="weight-calc-result">{g.toFixed(1)} g</div>
        <button onClick={() => onCompute(Math.round(g * 10) / 10)} type="button" className="btn-primary py-2 text-[11px]" data-testid="weight-calc-apply">
          Applica peso
        </button>
      </div>
    </div>
  );
}

/* ─── Product editor drawer ─── */
function ProductEditor({ product, onClose, onSaved }) {
  const [p, setP] = useState(() => ({
    name: product?.name || "",
    slug: product?.slug || "",
    description: product?.description || "",
    category: product?.category || SHOP_CATEGORIES[0].id,
    base_price_eur: product?.base_price_eur || 0,
    active: product?.active !== false,
    images: product?.images || [],
    variants: product?.variants?.length ? product.variants : [{ id: uid(), size_label: "", weight_g: 0, dimensions_note: "", price_delta_eur: 0, stock: 0 }],
    id: product?.id,
  }));
  const isNew = !p.id;  // Uses reactive state so it flips to false after first save
  const [saving, setSaving] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [showCalc, setShowCalc] = useState(null); // variant index
  const patchP = (k, v) => setP({ ...p, [k]: v });
  const patchVar = (i, k, v) => setP({ ...p, variants: p.variants.map((x, idx) => idx === i ? { ...x, [k]: v } : x) });
  const addVar = () => setP({ ...p, variants: [...p.variants, { id: uid(), size_label: "", weight_g: 0, dimensions_note: "", price_delta_eur: 0, stock: 0 }] });
  const removeVar = (i) => setP({ ...p, variants: p.variants.filter((_, idx) => idx !== i) });
  const removeImage = (i) => setP({ ...p, images: p.images.filter((_, idx) => idx !== i) });

  const uploadImage = async (file) => {
    if (!file) return;
    if (isNew) { toast.info("Prima salva il prodotto, poi potrai caricare le immagini"); return; }
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/shop/products/${p.id}/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((prev) => ({ ...prev, images: [...prev.images, r.data.url] }));
      toast.success("Immagine caricata");
    } catch (e) { toast.error(errMsg(e, "Upload fallito")); }
    finally { setUploadingImg(false); }
  };

  const save = async () => {
    if (!p.name.trim()) return toast.error("Nome prodotto obbligatorio");
    if (!(parseFloat(p.base_price_eur) > 0)) return toast.error("Prezzo base deve essere maggiore di 0");
    // Validate variants — surface WHICH one is invalid
    for (let i = 0; i < p.variants.length; i++) {
      const v = p.variants[i];
      if (!v.size_label.trim())     return toast.error(`Variante #${i + 1}: manca il nome (es. "Ø10cm")`);
      if (!(parseFloat(v.weight_g) > 0)) return toast.error(`Variante #${i + 1}: inserisci un peso in grammi maggiore di 0`);
    }
    setSaving(true);
    try {
      const payload = {
        name: p.name.trim(), slug: p.slug, description: p.description, category: p.category,
        base_price_eur: parseFloat(p.base_price_eur) || 0, active: p.active,
        images: p.images,
        variants: p.variants.map((v) => ({
          id: v.id, size_label: v.size_label.trim(),
          weight_g: parseFloat(v.weight_g) || 0,
          dimensions_note: v.dimensions_note || "",
          price_delta_eur: parseFloat(v.price_delta_eur) || 0,
          stock: parseInt(v.stock) || 0,
        })),
      };
      if (isNew) {
        const r = await api.post("/admin/shop/products", payload);
        toast.success("Prodotto creato · ora puoi caricare le immagini");
        setP((prev) => ({ ...prev, id: r.data.id, slug: r.data.slug }));
        onSaved();
      } else {
        await api.put(`/admin/shop/products/${p.id}`, payload);
        toast.success("Prodotto aggiornato");
        onSaved();
      }
    } catch (e) { toast.error(errMsg(e, "Errore salvataggio")); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[120] flex overflow-hidden" data-testid="product-editor">
      <div className="ml-auto w-full max-w-3xl bg-black border-l border-[#1a1a1a] overflow-y-auto">
        <div className="sticky top-0 bg-black border-b border-[#1a1a1a] p-6 flex items-center justify-between z-10">
          <div>
            <div className="font-mono-tech text-[10px] text-white/40 uppercase">{isNew ? "Nuovo prodotto" : `Modifica · ${p.slug}`}</div>
            <div className="font-display text-2xl">{p.name || "Senza nome"}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-2" data-testid="product-editor-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><label className="field-label">Nome prodotto *</label>
              <input className="field-boxed mt-2" value={p.name} onChange={(e) => patchP("name", e.target.value)} data-testid="prod-name" /></div>
            <div><label className="field-label">Categoria</label>
              <select className="field-boxed mt-2" value={p.category} onChange={(e) => patchP("category", e.target.value)} data-testid="prod-category">
                {SHOP_CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select></div>
            <div><label className="field-label">Prezzo base € (IVA inclusa) *</label>
              <input type="number" step="0.01" className="field-boxed mt-2" value={p.base_price_eur} onChange={(e) => patchP("base_price_eur", e.target.value)} data-testid="prod-price" /></div>
            <div className="md:col-span-2"><label className="field-label">Descrizione</label>
              <textarea className="field-boxed mt-2 h-24" value={p.description} onChange={(e) => patchP("description", e.target.value)} data-testid="prod-desc" /></div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="accent-[#ff4b00]" checked={p.active} onChange={(e) => patchP("active", e.target.checked)} />
              <span className="font-mono-tech text-xs text-white/70">Prodotto attivo (visibile nello shop)</span>
            </label>
          </div>

          {/* Images */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-tech text-[10px] uppercase text-white/60">Immagini ({p.images.length})</div>
              <label className="text-[10px] font-mono-tech text-[#ff4b00] cursor-pointer flex items-center gap-1" data-testid="prod-upload-label">
                <ImagePlus size={12} />
                {uploadingImg ? "Caricamento…" : "Aggiungi immagine"}
                <input type="file" hidden accept=".jpg,.jpeg,.png,.webp" onChange={(e) => uploadImage(e.target.files?.[0])} disabled={uploadingImg || isNew} data-testid="prod-upload-input" />
              </label>
            </div>
            {isNew && <div className="font-mono-tech text-[10px] text-white/40 mb-2">Salva prima il prodotto per abilitare l&apos;upload immagini.</div>}
            <div className="grid grid-cols-4 gap-2">
              {p.images.map((u, i) => (
                <div key={i} className="relative aspect-square border border-[#1a1a1a] bg-black group" data-testid={`prod-img-${i}`}>
                  <img src={fullMediaUrl(u)} alt="" className="w-full h-full object-contain" />
                  <button onClick={() => removeImage(i)} className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-black/80 p-1 hover:bg-red-500"><Trash2 size={10} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-tech text-[10px] uppercase text-white/60">Varianti *</div>
              <button onClick={addVar} className="text-[10px] font-mono-tech text-[#ff4b00] flex items-center gap-1" data-testid="prod-add-variant"><Plus size={12} /> Aggiungi</button>
            </div>
            {p.variants.map((v, i) => (
              <div key={v.id} className="border border-[#1a1a1a] p-4 mb-3 bg-[#0a0a0a]" data-testid={`prod-variant-${i}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-mono-tech text-[10px] text-white/40">Variante #{i + 1}</div>
                  {p.variants.length > 1 && (
                    <button onClick={() => removeVar(i)} className="text-white/40 hover:text-red-400" data-testid={`prod-var-remove-${i}`}><Trash2 size={12} /></button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div><label className="field-label">Nome variante (es. Ø10cm)</label>
                    <input className="field-boxed mt-1 text-sm" value={v.size_label} onChange={(e) => patchVar(i, "size_label", e.target.value)} data-testid={`prod-var-label-${i}`} /></div>
                  <div><label className="field-label">Peso (grammi) *</label>
                    <div className="flex gap-1 mt-1">
                      <input type="number" step="0.1" className="field-boxed flex-1 text-sm" value={v.weight_g} onChange={(e) => patchVar(i, "weight_g", e.target.value)} data-testid={`prod-var-weight-${i}`} />
                      <button onClick={() => setShowCalc(showCalc === i ? null : i)} type="button" className="btn-ghost px-3" data-testid={`prod-var-calc-${i}`} title="Calcola dal shape"><Calculator size={12} /></button>
                    </div></div>
                  <div><label className="field-label">Dimensioni note (mostrato al cliente)</label>
                    <input className="field-boxed mt-1 text-sm" value={v.dimensions_note} onChange={(e) => patchVar(i, "dimensions_note", e.target.value)} placeholder="Ø10cm · sp. 0,8mm" /></div>
                  <div><label className="field-label">Sovrapprezzo €</label>
                    <input type="number" step="0.01" className="field-boxed mt-1 text-sm" value={v.price_delta_eur} onChange={(e) => patchVar(i, "price_delta_eur", e.target.value)} /></div>
                  <div><label className="field-label">Stock (pezzi disponibili)</label>
                    <input type="number" className="field-boxed mt-1 text-sm" value={v.stock} onChange={(e) => patchVar(i, "stock", e.target.value)} data-testid={`prod-var-stock-${i}`} /></div>
                </div>
                {showCalc === i && (
                  <div className="mt-3">
                    <WeightCalculator onCompute={(g) => { patchVar(i, "weight_g", g); setShowCalc(null); toast.success(`Peso applicato: ${g}g`); }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="sticky bottom-0 bg-black border-t border-[#1a1a1a] pt-4 flex justify-end gap-3 -mx-6 px-6 pb-6">
            <button onClick={onClose} className="btn-ghost">Annulla</button>
            <button onClick={save} disabled={saving} className="btn-primary" data-testid="prod-save">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> {isNew ? "Crea prodotto" : "Salva"}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Orders Panel ─── */
function OrdersPanel() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/admin/shop/orders").then((r) => setOrders(r.data || [])).catch(() => setOrders([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    setBusy(id);
    try { await api.post(`/admin/shop/orders/${id}/status`, { status }); toast.success("Stato aggiornato"); await load(); }
    catch (e) { toast.error(errMsg(e, "Errore")); } finally { setBusy(null); }
  };
  if (loading) return <div className="text-white/40 font-mono-tech text-xs">Caricamento…</div>;
  if (!orders.length) return <div className="border border-[#1a1a1a] bg-[#0a0a0a] p-16 text-center text-white/50"><ShoppingBag size={40} className="mx-auto mb-4 text-white/20" />Ancora nessun ordine.</div>;
  return (
    <div className="border border-[#1a1a1a]" data-testid="shop-orders-panel">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-[#1a1a1a] text-white/50 font-mono-tech text-[10px] uppercase">
          <th className="text-left px-4 py-3">Data</th><th className="text-left px-4 py-3">Cliente</th>
          <th className="text-left px-4 py-3">Articoli</th><th className="text-right px-4 py-3">Totale</th>
          <th className="text-left px-4 py-3">Stato</th><th className="text-left px-4 py-3">Azioni</th>
        </tr></thead>
        <tbody>{orders.map((o) => (
          <tr key={o.id} className="border-b border-[#141414] hover:bg-white/5" data-testid={`shop-order-row-${o.id}`}>
            <td className="px-4 py-3 font-mono-tech text-[10px] text-white/60">{fmtDate(o.created_at)}</td>
            <td className="px-4 py-3"><div className="text-white">{o.customer.name}</div><div className="font-mono-tech text-[10px] text-white/40">{o.customer.email}</div></td>
            <td className="px-4 py-3 font-mono-tech text-[10px] text-white/60">{o.items.reduce((s, i) => s + i.qty, 0)} pezzi · {(o.total_weight_g / 1000).toFixed(1)}kg</td>
            <td className="px-4 py-3 text-right font-display text-lg text-[#ff4b00]">{fmtEur(o.total_eur)}</td>
            <td className="px-4 py-3"><span className="font-mono-tech text-[10px] uppercase border border-white/20 px-2 py-0.5">{o.status}</span></td>
            <td className="px-4 py-3">
              <select disabled={busy === o.id} value={o.status} onChange={(e) => setStatus(o.id, e.target.value)} className="bg-black border border-[#1a1a1a] px-2 py-1 text-[11px] text-white/80" data-testid={`shop-order-status-${o.id}`}>
                <option value="pending_payment">In attesa pagamento</option>
                <option value="awaiting_bank_transfer">Attesa bonifico</option>
                <option value="paid">Pagato</option>
                <option value="preparing">In preparazione</option>
                <option value="shipped">Spedito</option>
                <option value="delivered">Consegnato</option>
                <option value="canceled">Annullato</option>
                <option value="refunded">Rimborsato</option>
              </select>
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

/* ─── Shipping tiers editor ─── */
function ShopConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/shop/config").then((r) => setCfg(r.data));
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="text-white/40 font-mono-tech text-xs">Caricamento…</div>;
  const patchTier = (i, k, v) => setCfg({ ...cfg, shipping_tiers: cfg.shipping_tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) });
  const addTier = () => setCfg({ ...cfg, shipping_tiers: [...cfg.shipping_tiers, { max_g: null, cost_eur: 0, label: "" }] });
  const removeTier = (i) => setCfg({ ...cfg, shipping_tiers: cfg.shipping_tiers.filter((_, idx) => idx !== i) });
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg, shipping_tiers: cfg.shipping_tiers.map((t) => ({ ...t, max_g: t.max_g === "" || t.max_g === null ? null : parseInt(t.max_g), cost_eur: parseFloat(t.cost_eur) || 0 })) };
      await api.put("/admin/shop/config", payload);
      toast.success("Configurazione salvata");
      load();
    } catch (e) { toast.error(errMsg(e, "Errore")); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-6 max-w-4xl" data-testid="shop-config-panel">
      <div className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
        <div className="flex items-center justify-between mb-4">
          <div><div className="font-mono-tech text-[10px] uppercase text-white/40">Shop generale</div>
          <div className="font-display text-2xl">Impostazioni</div></div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="accent-[#ff4b00]" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="shop-enabled" />
            <span className="font-mono-tech text-xs text-white/70">Shop attivo</span>
          </label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="field-label">Soglia spedizione gratuita €</label>
            <input type="number" step="0.01" placeholder="Vuoto = disattiva" className="field-boxed mt-2" value={cfg.free_shipping_threshold_eur || ""} onChange={(e) => setCfg({ ...cfg, free_shipping_threshold_eur: e.target.value })} data-testid="shop-free-ship" /></div>
          <div><label className="field-label">Densità acciaio (g/cm³)</label>
            <input type="number" step="0.01" className="field-boxed mt-2" value={cfg.steel_density_g_cm3} onChange={(e) => setCfg({ ...cfg, steel_density_g_cm3: e.target.value })} /></div>
        </div>
      </div>
      <div className="border border-[#1a1a1a] p-6 bg-[#0a0a0a]">
        <div className="flex items-center justify-between mb-4">
          <div><Truck size={12} className="inline text-[#ff4b00] mr-2" /><span className="font-display text-lg">Fasce di spedizione (per peso)</span></div>
          <button onClick={addTier} className="text-[11px] font-mono-tech text-[#ff4b00] flex items-center gap-1"><Plus size={12} /> Aggiungi fascia</button>
        </div>
        <div className="space-y-2">
          <div className="grid grid-cols-12 gap-2 font-mono-tech text-[9px] uppercase text-white/40 px-2">
            <div className="col-span-4">Max peso (g)</div><div className="col-span-4">Costo €</div><div className="col-span-3">Etichetta</div><div className="col-span-1"></div>
          </div>
          {cfg.shipping_tiers.map((t, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`ship-tier-${i}`}>
              <input className="field-boxed col-span-4 text-sm" placeholder="vuoto = illimitato" value={t.max_g ?? ""} onChange={(e) => patchTier(i, "max_g", e.target.value)} />
              <input type="number" step="0.5" className="field-boxed col-span-4 text-sm" value={t.cost_eur} onChange={(e) => patchTier(i, "cost_eur", e.target.value)} />
              <input className="field-boxed col-span-3 text-sm" value={t.label} onChange={(e) => patchTier(i, "label", e.target.value)} />
              <button onClick={() => removeTier(i)} className="text-white/40 hover:text-red-400 col-span-1 text-right pr-2"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      </div>
      <button onClick={save} disabled={saving} className="btn-primary" data-testid="shop-config-save">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <><Save size={14} /> Salva configurazione</>}
      </button>
    </div>
  );
}

/* ─── Products Panel ─── */
function ProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/shop/products").then((r) => setProducts(r.data || [])).catch(() => setProducts([])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    try { await api.delete(`/admin/shop/products/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(errMsg(e, "Errore")); }
  };
  return (
    <div data-testid="shop-products-panel">
      <div className="flex items-center justify-between mb-6">
        <div className="font-mono-tech text-[10px] text-white/50 uppercase">{products.length} prodotti totali</div>
        <button onClick={() => setEditing({})} className="btn-primary" data-testid="shop-new-product"><Plus size={14} /> Nuovo prodotto</button>
      </div>
      {loading ? <div className="text-white/40 font-mono-tech text-xs">Caricamento…</div>
      : !products.length ? <div className="border border-dashed border-[#333] p-16 text-center text-white/50"><Package size={40} className="mx-auto mb-4 text-white/20" />Nessun prodotto ancora. Crea il primo!</div>
      : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {products.map((p) => (
            <div key={p.id} className="border border-[#1a1a1a] bg-[#0a0a0a]" data-testid={`shop-prod-row-${p.id}`}>
              <div className="aspect-video bg-black">
                {p.images?.[0] ? <img src={fullMediaUrl(p.images[0])} alt="" className="w-full h-full object-contain" /> : <Package className="m-auto mt-8 text-white/20" size={40} />}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="font-display text-lg truncate">{p.name}</div>
                  {p.active ? <span className="text-[9px] font-mono-tech text-green-400 border border-green-400/40 px-1.5 py-0.5">ONLINE</span>
                              : <span className="text-[9px] font-mono-tech text-white/40 border border-white/20 px-1.5 py-0.5">OFF</span>}
                </div>
                <div className="font-mono-tech text-[10px] text-white/40 mb-2">{SHOP_CATEGORIES.find((c) => c.id === p.category)?.label || p.category} · {p.variants?.length || 0} variant{p.variants?.length !== 1 ? "i" : "e"}</div>
                <div className="font-display text-xl text-[#ff4b00] mb-3">{fmtEur(p.base_price_eur)}</div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(p)} className="btn-ghost flex-1 py-1.5 text-[10px]" data-testid={`prod-edit-${p.id}`}><Edit3 size={11} /> Modifica</button>
                  <button onClick={() => del(p.id)} className="btn-ghost py-1.5 px-3 hover:text-red-400"><Trash2 size={11} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSaved={() => { load(); }} />}
    </div>
  );
}

/* ─── Root component ─── */
export default function AdminShop() {
  const [subTab, setSubTab] = useState("products");
  return (
    <div data-testid="admin-shop">
      <div className="flex items-center gap-2 mb-6 border-b border-[#1a1a1a]">
        {[
          ["products", "Prodotti", ShoppingBag],
          ["orders",   "Ordini",   Truck],
          ["config",   "Config Shop", Settings],
        ].map(([k, l, Ic]) => (
          <button key={k} onClick={() => setSubTab(k)} data-testid={`shop-subtab-${k}`}
            className={`px-4 py-3 font-mono-tech text-[11px] uppercase flex items-center gap-2 ${subTab === k ? "text-[#ff4b00] border-b-2 border-[#ff4b00] -mb-px" : "text-white/50 hover:text-white"}`}>
            <Ic size={12} /> {l}
          </button>
        ))}
      </div>
      {subTab === "products" && <ProductsPanel />}
      {subTab === "orders" && <OrdersPanel />}
      {subTab === "config" && <ShopConfigPanel />}
    </div>
  );
}
